import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { Select } from '@/components/ui/Select';
import { Button } from '@/components/ui/Button';
import { t } from '@/lib/i18n';
import type { EditorState } from '../roleContractEditorState';
import { DECISION_ACTIONS, NOT_SET } from '../roleContractEditorState';

interface Props {
  state: EditorState;
  setState: React.Dispatch<React.SetStateAction<EditorState>>;
}

// Sven dogfood 2026-05-03: founders asked "what's a sensible default?"
// These presets match the operating-mode philosophy — Founder owns
// every decision, Copilot escalates the heavy ones, Autopilot runs
// everything but root/trunk autonomously.
const DECISION_PRESETS: Record<string, { root: string; trunk: string; branch: string; leaf: string }> = {
  founder: {
    root: 'Autonomous',
    trunk: 'Autonomous',
    branch: 'Autonomous',
    leaf: 'Autonomous',
  },
  copilot: {
    root: 'Must escalate',
    trunk: 'Must escalate',
    branch: 'Requires approval',
    leaf: 'Autonomous',
  },
  autopilot: {
    root: 'Must escalate',
    trunk: 'Must escalate',
    branch: 'Autonomous',
    leaf: 'Autonomous',
  },
};

export function DecisionRightsSection({ state, setState }: Props) {
  function applyPreset(name: keyof typeof DECISION_PRESETS) {
    const preset = DECISION_PRESETS[name];
    setState((s) => ({
      ...s,
      decisionRights: { ...preset },
    }));
  }

  return (
    <Card>
      <CardHeader><CardTitle>{t('Decision Rights')}</CardTitle></CardHeader>
      <CardContent className="space-y-4">
        <p className="text-sm text-muted-foreground">
          {t('How this role handles decisions at each impact tier. Root = existential (alters the company\'s purpose). Trunk = strategic (1–3 year direction). Branch = tactical (reversible in weeks). Leaf = operational (day-to-day).')}
        </p>
        <div>
          <p className="text-xs font-medium text-muted-foreground mb-1.5">
            {t('Quick start — apply a preset, then fine-tune below:')}
          </p>
          <div className="flex flex-wrap gap-2">
            <Button type="button" variant="outline" size="sm" onClick={() => applyPreset('founder')}>
              {t('Founder mode')}
            </Button>
            <Button type="button" variant="outline" size="sm" onClick={() => applyPreset('copilot')}>
              {t('Copilot mode')}
            </Button>
            <Button type="button" variant="outline" size="sm" onClick={() => applyPreset('autopilot')}>
              {t('Autopilot mode')}
            </Button>
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {(['root', 'trunk', 'branch', 'leaf'] as const).map((level) => (
            <Select
              key={level}
              label={t(`Impact: ${level}`)}
              value={state.decisionRights[level] || NOT_SET}
              onValueChange={(v) =>
                setState((s) => ({
                  ...s,
                  decisionRights: { ...s.decisionRights, [level]: v === NOT_SET ? '' : v },
                }))
              }
              options={DECISION_ACTIONS}
            />
          ))}
        </div>
      </CardContent>
    </Card>
  );
}
